import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Icon_CheckmarkFilled60,
  isTouchDevice,
  LogSentryException,
  Modal,
  ServiceError,
  TextField,
} from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { queryKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { useLogout } from "../../hooks/useAuth";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { UMSrequestClient } from "../../stores/requestClient";
import { ServiceErrorCode } from "../../types";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";
import {
  passwordSchema,
  ValidatedPasswordInput,
} from "../ValidatedPasswordInput/ValidatedPasswordInput";

import style from "./PasswordChangeModal.module.css";

export type ChangePasswordRequestPayload = {
  newPassword: string;
  oldPassword: string;
  captcha?: string;
};

export default function PasswordChangeModal({
  onClose,
  open,
}: {
  onClose: () => void;
  open: boolean;
}) {
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const tracker = useTracker();

  const schema = useMemo(
    () =>
      z
        .object({
          currentPassword: z.string({
            required_error: t(m.Account.PasswordChange.ValidationCurrentPassword),
          }),
          newPassword: passwordSchema,
          newPasswordConfirmation: z.string(),
          captcha: z.string().optional(),
        })
        .refine((data) => data.newPassword === data.newPasswordConfirmation, {
          path: ["newPasswordConfirmation"],
          message: t(m.Account.PasswordChange.ValidationPasswordMismatch),
        })
        .refine((data) => data.currentPassword !== data.newPassword, {
          path: ["newPasswordConfirmation"],
          message: t(m.ErrorMessage.PasswordUnchanged),
        }),
    [t, m]
  );

  type PasswordChangeFormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
  } = useForm<PasswordChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
      captcha: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        currentPassword: "",
        newPassword: "",
        newPasswordConfirmation: "",
        captcha: "",
      });
    }
  }, [open, reset]);

  const { mutateAsync: mutateLogout, isPending: isLogoutPending } = useLogout({
    redirect: false,
    withRequest: true,
  });

  const {
    mutateAsync: mutateChangePassword,
    isSuccess: isChangePasswordSuccess,
    error: changePasswordError,
  } = useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "change-password"],
    mutationFn: ({ currentPassword, newPassword, captcha }: PasswordChangeFormData) =>
      UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/change-password`,
        body: {
          newPassword,
          oldPassword: currentPassword,
          captcha,
        } satisfies ChangePasswordRequestPayload,
      }),
  });

  const passwordError =
    changePasswordError instanceof ServiceError &&
    changePasswordError.serviceErrorResponse.message === "Bad credentials"
      ? t(m.ErrorMessage.InvalidPassword)
      : undefined;
  const [displayCaptchaError, setDisplayCaptchaError] = useState(false);

  let errorMessage = null;
  if (changePasswordError && !(changePasswordError instanceof ServiceError)) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }
  if (
    (changePasswordError instanceof ServiceError &&
      changePasswordError.hasCode(ServiceErrorCode.CaptchaMissmatch)) ||
    displayCaptchaError
  ) {
    errorMessage = t(m.ErrorMessage.CaptchaError);
  }
  if (
    changePasswordError instanceof ServiceError &&
    changePasswordError.hasCode(ServiceErrorCode.PasswordUnchanged)
  ) {
    errorMessage = t(m.ErrorMessage.PasswordUnchanged);
  }

  const submit = async (data: PasswordChangeFormData) => {
    let recaptchaValue: string | undefined;
    try {
      recaptchaValue = await recaptchaRef?.current?.execute();
      try {
        tracker.trackButtonClick({
          location: ElementClickLocation.CHANGE_PASSWORD_MODAL,
          action: ElementClickAction.CONFIRM,
        });
        await mutateChangePassword({ ...data, captcha: recaptchaValue });
      } catch (error) {
        // This is to not throw Unhandled Promise Rejection error
      }
    } catch (error) {
      setDisplayCaptchaError(true);
      LogSentryException({ messagePrefix: "Captcha error", error });
    }
  };

  const closeCallback = useCallback(async () => {
    if (isChangePasswordSuccess) {
      await mutateLogout();
      navigation.goToSignIn();
    }
    onClose();
  }, [isChangePasswordSuccess, navigation, onClose, mutateLogout]);

  const isInputsDirty =
    dirtyFields.currentPassword && dirtyFields.newPassword && dirtyFields.newPasswordConfirmation;
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  return (
    <Modal
      open={open}
      title={t(m.Account.PasswordChange.Title)}
      size="small"
      onClose={closeCallback}
    >
      {!isChangePasswordSuccess && (
        <form className={style.passwordChangeForm} onSubmit={handleSubmit(submit)}>
          <TextField
            type="password"
            placeholder={t(m.Account.PasswordChange.CurrentPassword)}
            {...register("currentPassword")}
            errorMessage={passwordError}
            autoFocus={!isTouchDevice()}
          ></TextField>
          <div></div>
          <ValidatedPasswordInput
            placeholder={t(m.Account.PasswordChange.NewPassword)}
            {...register("newPassword")}
            error={Boolean(errors.newPassword)}
          ></ValidatedPasswordInput>
          <TextField
            type="password"
            placeholder={t(m.Account.PasswordChange.ReEnterNewPassword)}
            {...register("newPasswordConfirmation")}
            errorMessage={errors.newPasswordConfirmation?.message}
          ></TextField>
          <Button type="submit" disabled={!isInputsDirty || isSubmitting}>
            {t(m.Account.PasswordChange.UpdatePassword)}
          </Button>
          <ReCAPTCHA ref={recaptchaRef} />
          {errorMessage && <div className={style.error}>{errorMessage}</div>}
        </form>
      )}
      {isChangePasswordSuccess && (
        <div className={style.passwordChangeSuccessContainer}>
          <Icon_CheckmarkFilled60 className={style.image} />
          <div className={style.messageContainer}>
            <span>{t(m.Account.PasswordChange.Success)}</span>
          </div>
          <Button variant="grayscale" onClick={closeCallback} disabled={isLogoutPending}>
            {t(m.Account.PasswordChange.Close)}
          </Button>
        </div>
      )}
    </Modal>
  );
}
